/**
 * Timesheet itinerary grouping — turns the flat chronological time entries into
 * a day itinerary: drive legs + store visits, each visit nesting its projects
 * (combined minutes) and each project its discrete clockings.
 *
 * Groups the SAME entries the flat list renders — nothing is recomputed — so a
 * visit's/day's totals always equal the flat totals (guaranteed by construction;
 * the timesheet asserts this and falls back to the flat view if it ever fails).
 *
 * A visit = a run of same-store WORK rows uninterrupted by a drive. A drive row
 * (the rep left) or a different store closes the current visit, so two visits to
 * one store in a day — separated by a drive away and back — are two visits, not
 * merged. Drive rows are standalone timeline legs in chronological position.
 */

export interface ItineraryEntry {
  id: number;
  start: string;
  end: string;
  durationMin: number;
  duration: string;
  callLogId?: number;
  projectId?: number | null;
  projectDescription?: string;
  miles?: number;
  /**
   * Shown to the rep, but left out of every total. Set upstream by
   * `processCallLogs`, which is the only place holding the odometer evidence the
   * decision rests on — nothing downstream re-judges the drive.
   */
  excludedFromTotals?: boolean;
  type: 'store' | 'drive';
  store: string;
}

export interface ItineraryClocking {
  id: number;
  start: string;
  end: string;
  durationMin: number;
  callLogId?: number;
}

export interface ItineraryProject {
  projectId: number | null;
  projectDescription?: string;
  totalMin: number;
  clockings: ItineraryClocking[];
}

export interface ItineraryVisit {
  kind: 'visit';
  store: string;
  checkIn: string;
  checkOut: string;
  totalMin: number;
  hasActive: boolean;
  projects: ItineraryProject[];
}

export interface ItineraryDrive {
  kind: 'drive';
  /**
   * Row key only. NOT a CallLog id — processCallLogs mints it as
   * `calllogid * 10 (+1 for Drive Back)` so the outbound and return legs of one
   * visit stay distinct. Use `callLogId` to reach the visit (#3519).
   */
  id: number;
  /** The visit this drive is recorded on. */
  callLogId?: number;
  label: string; // 'Drive' | 'Drive Back'
  start: string;
  end: string;
  durationMin: number;
  miles?: number;
  excludedFromTotals?: boolean;
}

export type ItineraryItem = ItineraryVisit | ItineraryDrive;

export function buildItinerary(entries: ItineraryEntry[]): ItineraryItem[] {
  const items: ItineraryItem[] = [];
  let current: ItineraryVisit | null = null;

  const flush = () => {
    if (current) {
      items.push(current);
      current = null;
    }
  };

  for (const e of entries) {
    if (e.type === 'drive') {
      // A drive means the rep left — close the current visit, then place the
      // drive leg in chronological position.
      flush();
      items.push({
        kind: 'drive',
        id: e.id,
        callLogId: e.callLogId,
        label: e.store, // 'Drive' | 'Drive Back'
        start: e.start,
        end: e.end,
        durationMin: e.durationMin,
        miles: e.miles,
        excludedFromTotals: e.excludedFromTotals,
      });
      continue;
    }

    // Store/work row. Start a new visit when the store changes.
    if (!current || current.store !== e.store) {
      flush();
      current = {
        kind: 'visit',
        store: e.store,
        checkIn: e.start,
        checkOut: e.end,
        totalMin: 0,
        hasActive: false,
        projects: [],
      };
    }

    current.checkOut = e.end;
    current.totalMin += e.durationMin;
    if (e.duration === 'Active' || e.end === 'In Progress') current.hasActive = true;

    let proj = current.projects.find((p) => p.projectId === (e.projectId ?? null));
    if (!proj) {
      proj = {
        projectId: e.projectId ?? null,
        projectDescription: e.projectDescription,
        totalMin: 0,
        clockings: [],
      };
      current.projects.push(proj);
    }
    proj.totalMin += e.durationMin;
    proj.clockings.push({
      id: e.id,
      start: e.start,
      end: e.end,
      durationMin: e.durationMin,
      callLogId: e.callLogId,
    });
  }

  flush();
  return items;
}

/** Sum store + drive minutes across an itinerary, for the totals-match guard. */
export function itineraryTotals(items: ItineraryItem[]): { storeMin: number; driveMin: number } {
  let storeMin = 0;
  let driveMin = 0;
  for (const it of items) {
    if (it.kind === 'visit') storeMin += it.totalMin;
    else if (!it.excludedFromTotals) driveMin += it.durationMin;
  }
  return { storeMin, driveMin };
}
